/**
 * @file 网站通用配置，依赖后端接口
 * @date 2021-07-03
 */

import getSymbols from '@bfe/trade-base-symbol-list';
import { commonapi as bybitapi } from '@/apis/api2';
import { leverageApi } from '@/apis/spot';
import { leverageApi as unifyLeverageApi } from '@/apis/unified';
import { i18n } from '@/locales/i18n';
import { create as createBackoff, get as getBackoff, remove as removeBackoff } from '@/utils/backoff';
import { div } from '@/utils/number';

const state = {

    /**
     * 是否展示0费率，该字段暂时弃用，改为后台管理配置
     */
    showZeroFee: false,

    /**
     * 当前法币为计价货币的列表
     */

    fiatCurrencys: null,

    /**
     * 所有法币集合
     */
    allFiatCurrency: null,
    /**
     * 当前计价货币列表，以 `quoteTokenName` 为 key
     *
     * - null 默认未加载
     * - [] 空数据
     * - ['BTC', ...]
     */
    quoteCurrencys: null,

    /**
     * 当前各个计价货币 quoteTokenName 包含的标签列表
     *
     * - null 默认未加载
     * - {} 空数据
     * - { USDT: [ 'tag1', 'tag2' ], ... }
     */
    quoteTags: null,

    /**
     * 交易对列表，使用时请注意判断：`showStatus` 、`noticeVisible`
     *
     * - null 默认未加载
     * - [] 空数据
     * - [{}, ...]
     */
    symbols: null,

    /**
     * 交易对列表 Map
     */
    symbolsMap: null,

    /**
     * 借贷币种列表
     *
     * - null 默认未加载
     * - [] 空数据
     * - [{}, ...]
     */
    loanTokenList: null,

    /**
     * 资产币种列表
     *
     * - null 默认未加载
     * - [] 空数据
     * - [{}, ...]
     */
    assetCurrency: null,

    /**
     * 当前法币名称，在登录后、退出后、语言切换时（`App.vue`）重新计算
     * 规则：用户配置>语言识别
     */
    legalName: '',

    /**
     * 以交易对为 key 的对 USDT 汇率（行情），由于行情数据以 USDT 为基础，而法币汇率接口是以 USD 为单位，这里默认即为 1:1
     *
     * @description
     *  由于交易页目前只存在以计价货币为基础单位的折合（via. USDT、BTC、ETH）
     *  而目前所有计价货币对 USDT 都有交易对，所以只存 计价货币->USDT 交易对汇率即可
     *  1. 行情数据先返回：由于不确认哪个币种是计价货币，会先把全量行情数据写入到这里，并在交易对成功后计算出所有计价货币->USDT 汇率
     *  2. 行情数据在交易对数据返回后：已经明确知道哪些是计价货币，只存储计价货币->USDT 汇率
     * @type {Object}
     * @example
     *  {
     *      USDTUSDT: '1',
     *      ETHUSDT: '31576.11',
     *      BTCUSDT: '44149',
     *  }
     */
    symbol2usdt: {
        USDTUSDT: '1',
    },

    /**
     * 以法币种名称为 key ，USD->法币汇率，依赖 `@api2/api/exchange-rate?name=法币`
     *
     * @type {Object}
     * @example
     *  {
     *      CNY: '6.59998',
     *      USD: '1',
     *  }
     */
    usd2legal: {
        USD: '1',
    },

    /**
     * 页面状态
     *
     */
    pageStatus: {
        show: true, // 页面是否可见
        hide_ts: 0, // 隐藏时间用来计算页面从隐藏到显示的时间差
        show_diff: 0, // 隐藏到可见时间
        online: true, // 是否在线
        offline_ts: 0, // 离线开始时间
        online_diff: 0, // 离线时间到在线时间差
    },
    // 全局设置信息
    globalSetting: {
        show: false, // 是否展示全局设置弹窗
    },

    // 计价币的信息
    qouteTokenInfo: {

    },
};

const getters = {
    symbols(state) {
        return state.symbols || [];
    },
    symbolsMap(state) {
        return state.symbolsMap || [];
    },
    loanTokenList(state) {
        return state.loanTokenList || [];
    },
    assetCurrency(state) {
        return state.assetCurrency || [];
    },
    quoteCurrencys(state) {
        return state.quoteCurrencys || [];
    },
    symbolMap(state, getters) {
        return getters.symbols.reduce((data, item) => {
            data[item.symbolId] = item;
            return data;
        }, {});
    },
    loanTokenListMap(state, getters) {
        return getters.loanTokenList.reduce((data, item) => {
            data[item.tokenId] = item;
            return data;
        }, {});
    },
    assetCurrencyMap(state, getters) {
        return getters.assetCurrency.reduce((data, item) => {
            data[item.coin] = item;
            return data;
        }, {});
    },
};

const mutations = {
    SET_ALL_FIAT_CURRENCY(state, payload) {
        state.allFiatCurrency = payload;
    },
    SET_SYMBOLS(state, payload) {
        state.symbols = payload;
        if (payload && Array.isArray(payload)) {
            const symbolsMap = new Map();
            let i = 0;
            while (i < payload.length) {
                const element = payload[i];
                const { symbolId = null } = element;
                if (symbolId) {
                    symbolsMap.set(symbolId, element);
                }
                i += 1;
            }
            state.symbolsMap = symbolsMap;
        }
    },
    SET_SYMBOL_BY_ID(state, payload = {}) {
        const index = state.symbols.findIndex((item) => item.symbolId === payload.symbolId);
        if (index > -1) state.symbols[index] = payload;
    },
    SET_QUOTE_CURRENCYS(state, payload) {
        state.quoteCurrencys = payload;
    },
    SET_FIAT_CURRENCYS(state, payload) {
        state.fiatCurrencys = payload;
    },
    SET_QUOTE_TAGS(state, payload) {
        state.quoteTags = payload;
    },
    SET_LOAN_TOKEN_LIST(state, payload) {
        state.loanTokenList = payload;
    },
    SET_ASSET_CURRENCY(state, payload) {
        state.assetCurrency = payload;
    },
    SET_LEGAL_NAME(state, payload) {
        state.legalName = payload;
    },
    SET_SYMBOL2USDT(state, payload) {
        payload.forEach(({ symbolId, close }) => {
            state.symbol2usdt[symbolId] = close;
        });
    },
    SET_USD2LEGAL(state, { key, value } = {}) {
        state.usd2legal[key] = value;
    },

    // 设置页面状态
    SET_PAGE_STATUS(state, payload) {
        // 设置页面隐藏显示状态
        if (payload.type === 'show') {
            state.pageStatus.show = !payload.status;

            // 页面隐藏记录隐藏时间
            if (payload.status) {
                state.pageStatus.hide_ts = payload.ts;
                state.pageStatus.show_diff = 0;
                // 页面可见计算隐藏到可见时间差
            } else {
                state.pageStatus.show_diff = parseInt(payload.ts - state.pageStatus.hide_ts, 10);
            }
            // 设置页面在线离线状态
        } else if (payload.type === 'online') {
            state.pageStatus.online = payload.status;

            // 有网
            if (payload.status) {
                state.pageStatus.online_diff = parseInt(payload.ts - state.pageStatus.offline_ts, 10);
                // 断网记录时间
            } else {
                state.pageStatus.online_diff = 0;
                state.pageStatus.offline_ts = payload.ts;
            }
        }
    },
    // 设置全局设置弹窗
    SET_GLOBAL_SETTING(state, payload) {
        if (typeof payload === 'object' && !Array.isArray(payload)) {
            Object.assign(state.globalSetting, payload);
        }
    },
    // 设置计价代币的信息（比如USDT的图标之类的）
    SET_QOUTE_TOKEN_INFO(state, payload) {
        if (typeof payload === 'object' && !Array.isArray(payload)) {
            Object.assign(state.qouteTokenInfo, payload);
        }
    },
};

const actions = {
    async GET_SYMBOLS({ commit }) {
        const { all: result, handleSymbols } = await getSymbols(i18n.global.locale.value);

        const {
            symbols,
            quoteCurrencys,
            quoteTags,
            fiatCurrency,
            allFiatCurrency,
            qouteTokenInfo,
        } = handleSymbols(result);

        commit('SET_SYMBOLS', symbols);
        commit('SET_QUOTE_CURRENCYS', quoteCurrencys);
        commit('SET_FIAT_CURRENCYS', fiatCurrency);
        commit('SET_ALL_FIAT_CURRENCY', allFiatCurrency);
        commit('SET_QUOTE_TAGS', quoteTags);
        commit('SET_QOUTE_TOKEN_INFO', qouteTokenInfo);
    },

    INIT_LEGAL({
        commit, dispatch, rootState, state,
    }) {
        const mapping = {
            en: 'USD',
            'zh-MY': 'CNY',
            'zh-HK': 'CNY',
            'zh-TW': 'CNY',
        };

        if (rootState.user.userstate === 0) return; // 如果用户状态还没有初始化完成，则不设置法币

        const currentLegal = state.legalName;
        if (rootState.user.userstate === 1 && rootState.user.info.currency_code) {
            commit('SET_LEGAL_NAME', rootState.user.info.currency_code.toUpperCase());
        } else if (i18n.global.locale.value) {
            commit('SET_LEGAL_NAME', mapping[i18n.global.locale.value] || mapping.en);
        } else {
            commit('SET_LEGAL_NAME', mapping.en);
        }

        // 法币种更新时同步汇率
        if (state.legalName !== currentLegal) dispatch('SYNC_LEGAL_RATES');
    },

    // 更新 USDT 交易对行情数据到汇率中
    UPDATE_SYMBOL2USDT({ commit, state, getters }, data) {
        const { quoteCurrencys } = state;
        const { symbolMap } = getters;
        const symbol2usdt = [];
        const map = {};
        const isNotLoadedQuote = quoteCurrencys === null || quoteCurrencys.length === 0;

        data.forEach((item) => {
            const symbol = symbolMap[item.s];

            // 忽略非 USDT 交易对
            // 如果在交易对数据里不存在，也忽略

            if (!symbol || (symbol.quoteTokenId !== 'USDT' && symbol.baseTokenId !== 'USDT')) return;

            // 没有计价货币时，设置全量交易对数据，因为不确定哪个是计价货币
            if (isNotLoadedQuote || quoteCurrencys?.find((currency) => currency.value === symbol.baseTokenId)) {
                const isExit = map[`${symbol.baseTokenId}USDT`] || map[`USDT${symbol.quoteTokenId}`];
                if (!isExit) {
                    const symbolId = symbol.quoteTokenId === 'USDT' ? item.s
                        : `${symbol.quoteTokenId}${symbol.baseTokenId}`;
                    symbol2usdt.push({
                        symbolId,
                        close: symbol.quoteTokenId === 'USDT' ? item.c : div(1, item.c),
                    });
                    map[symbolId] = true;
                }
            }
        });

        if (symbol2usdt.length) commit('SET_SYMBOL2USDT', symbol2usdt);
    },

    // 同步【USD->法币】汇率
    async SYNC_LEGAL_RATES({ state, commit }) {
        const { legalName } = state;
        if (!legalName) return;

        const res = await bybitapi.getLegalRate(legalName);
        if (!res.success || !res.result) return;
        commit('SET_USD2LEGAL', { key: legalName, value: res.result });
    },

    // 获取 全仓杠杆 借贷币种列表信息
    async GET_LEVERAGE_TOKENS({ rootGetters, commit, dispatch }) {
        // 获取是否已升级统一账户的状态
        const isUTA = rootGetters['user/isUTA'];
        let res = null;

        if (isUTA) {
            // 升级统一账户后
            res = await unifyLeverageApi.getTokenList();
        } else {
            // 未升级前
            res = await leverageApi.getTokenList();
        }
        const { success, result } = res;
        if (success) {
            if (result?.length) {
                commit('SET_LOAN_TOKEN_LIST', result);
                removeBackoff('GET_LEVERAGE_TOKENS');
            }
        } else if (!getBackoff('GET_LEVERAGE_TOKENS')) {
            createBackoff('GET_LEVERAGE_TOKENS', {
                min: 1000,
                max: 10000,
                maxCount: 3,
                callback() {
                    return dispatch('GET_LEVERAGE_TOKENS');
                },
            });
        }
    },

    // 获取 资产币种列表
    async GET_ASSET_CURRENCY({ commit, state, dispatch }) {
        const { assetCurrency } = state;
        if (assetCurrency) return;
        const res = await bybitapi.getAssetCurrencyList({ accountType: 'ACCOUNT_TYPE_SPOT' });
        const { success, result } = res;
        if (success) {
            if (result?.coins?.length) {
                commit('SET_ASSET_CURRENCY', result.coins);
            }
        } else if (!getBackoff('GET_ASSET_CURRENCY')) {
            createBackoff('GET_ASSET_CURRENCY', {
                min: 1000,
                max: 10000,
                maxCount: 3,
                callback() {
                    return dispatch('GET_ASSET_CURRENCY');
                },
            });
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
